// EsimSection.jsx
 "use client";

import { useMemo, useCallback, useEffect, useState } from "react";
import Link from "next/link";
import EsimFilters from "./EsimFilters";
import EsimFilterDrawer from "./EsimFilterDrawer";
import ProductList from "@/components/ProductList";
import { useEsimOffersClient } from "../hooks/useEsimsClient";
import { useEsimOffers } from "../hooks/useEsims";
import { enrichEsimOffer } from "../utils/enrichEsimOffer";
import { fetchAllEsimOffers } from "@/utils/api/esim";
import { countryName, toA2 } from "@/features/landing/utils/country-name";

const uniq = (arr) => Array.from(new Set(arr)).sort();

const ESIM_BRAND = "ReloadPi ESIM";


// "Western Europe eSIM by ReloadPi" / "Japan eSIM by ReloadPi" / "Global eSIM by ReloadPi"
const makeEsimTitle = (regions = [], countryLabel = "", brand = ESIM_BRAND) => {
  const firstRegion = Array.isArray(regions) && regions.length ? regions[0] : "";
  const where = firstRegion || countryLabel || "Global";
  return `${where} eSIM by ${brand}`;
};


export default function EsimSectionClient({ initial, initialAllOffers,
  brands,
  selectedProduct,
  onSelect,
  user,
  piRate,
  feePercent,
}) {
  // 1) Fetch page data
  const { esims, total, loading, error, filters, setFilters, setFilter, limit, offset } =
  useEsimOffersClient(initial, { limit: initial?.limit ?? 20 });

  // 2) Fetch ALL offers once for facets
// 2) Fetch ALL offers once for facets (lazy, tab-only)
const [allOffers, setAllOffers] = useState(initialAllOffers || []);
const [allLoading, setAllLoading] = useState(!initialAllOffers?.length);
const [allError, setAllError] = useState("");


useEffect(() => {
  if (initialAllOffers?.length) {
    setAllLoading(false);
    return;
  }

  const controller = new AbortController();
  let alive = true;

  (async () => {
    try {
      setAllLoading(true);
      setAllError("");

      const offers = await fetchAllEsimOffers({ signal: controller.signal });
      if (alive) setAllOffers(Array.isArray(offers) ? offers : []);
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.error("❌ Failed to load ALL eSIM offers:", e);
      if (alive) setAllError("failed");
    } finally {
      if (alive) setAllLoading(false);
    }
  })();

  return () => {
    alive = false;
    controller.abort();
  };
}, [initialAllOffers]);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Plan type: "all" | "country" (single-country) | "regional" (multi-country bundles)
  const [planType, setPlanType] = useState("all");
  const handlePlanTypeChange = useCallback(
    (next) => {
      setPlanType(next);
      // Regional bundles have no single country code — clear any country filter.
      setFilters((f) => ({
        ...f,
        offset: 0,
        ...(next === "regional" ? { country: "" } : {}),
      }));
    },
    [setFilters]
  );

  // 3) Normalize all offers for facets
const allNorm = useMemo(() => {
  const source = allOffers.length ? allOffers : (esims || []);
  return source.map((o) => ({
    country: toA2(o.countryName || o.country || o.countryCode || ""),
    regions: Array.isArray(o.regions) ? o.regions.filter(Boolean) : [],
    subTypes: Array.isArray(o.subTypes) ? o.subTypes.filter(Boolean) : [],
  }));
}, [allOffers, esims]);



  // 4) Compute smart options based on current selection
  const options = useMemo(() => {
    let pool = allNorm;

    if (filters.regions) pool = pool.filter((o) => o.regions.includes(filters.regions));
    if (filters.country) pool = pool.filter((o) => o.country === filters.country);
    if (filters.subType) pool = pool.filter((o) => o.subTypes.includes(filters.subType));
    const countries = uniq(pool.map((o) => o.country).filter(Boolean)).map((code) => ({
      code,
      name: countryName(code),
    }));
      countries.sort((a, b) => a.name.localeCompare(b.name));

    const regions = uniq(pool.flatMap((o) => o.regions).filter(Boolean));
    const subTypes = uniq(pool.flatMap((o) => o.subTypes).filter(Boolean));

    return { countries, regions, subTypes };
 }, [allNorm, filters.country, filters.regions, filters.subType]);

  // Region values that actually have multi-country bundles (country === "").
  const regionalRegions = useMemo(
    () => uniq(allNorm.filter((o) => !o.country).flatMap((o) => o.regions).filter(Boolean)),
    [allNorm]
  );

  const filtersReady = !allLoading && allOffers.length > 0;

  // 5) Auto-clear invalid selections
  useEffect(() => {
    if (filters.country && !options.countries.some((c) => c.code === filters.country)) {
      setFilter("country", "");
    }
    if (filters.regions && !options.regions.includes(filters.regions)) {
      setFilter("regions", "");
    }
    if (filters.subType && !options.subTypes.includes(filters.subType)) {
      setFilter("subType", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  // 6) Enrich current page
 const safeNormalize = useCallback(
  (o) => {
    const brandCode = (o.brand || o.brandCode || o.brand_id || "")
      .toString()
      .trim()
      .toLowerCase();
    const b = brands?.[brandCode] || {};

    const regions = Array.isArray(o.regions) ? o.regions.filter(Boolean) : [];
    const countryA2 = toA2(o.countryName || o.country || o.countryCode) || "";
    const countryLabel = countryA2 ? countryName(countryA2) : "";
     const roamingCount = Number.isFinite(o.roamingCount) ? o.roamingCount : 0;
      const isRegional = !countryA2 && roamingCount > 0;

    const apiName = (o.name || "").trim();

    return {
      type: "ESIM",
      productType: "ESIM",

      brandName: ESIM_BRAND,
      uiBrand: ESIM_BRAND,
      uiTitle: isRegional
  ? `${regions[0] || "Regional"} eSIM by ${brand} • covers ${roamingCount} countries`
  : makeEsimTitle(regions, countryLabel || countryA2),

      offerId:
        o.offerId ||
        o.id ||
        o._id ||
        `${brandCode}-${o.durationDays || o.duration || "unk"}`,

      // if Zendit sends just "eSIM" as name, ignore it and use our title instead
      name:
        apiName && apiName.toLowerCase() !== "esim"
          ? apiName
          : makeEsimTitle(regions, countryLabel || countryA2),

      image: b.image || o.image || null,
      priceUSD:
        o.priceUSD ?? (o.price?.fixed != null ? o.price.fixed / 100 : null),
      durationDays: o.durationDays ?? o.duration ?? o.validityDays ?? null,
      dataGB: o.dataGB ?? o.data_gb ?? null,
      dataUnlimited: !!(o.dataUnlimited ?? o.unlimited),
      dataSpeeds: o.dataSpeeds ?? [],
      subTypes: Array.isArray(o.subTypes) ? o.subTypes : [],
      country: countryA2,
      regions,
      shortNotes: o.shortNotes || "",
      notes: o.notes || "",
    };
  },
  [brands]
);

const enrichedEsims = useMemo(() => {
  return (esims || []).map((o) => {
    try {
      const rich = enrichEsimOffer(o, brands);
      const base = rich && typeof rich === "object" ? rich : safeNormalize(o);

      const regions = Array.isArray(base.regions) ? base.regions : [];
       const countryA2 = toA2(o.countryName || o.country || o.countryCode) || "";
      const countryLabel = countryA2 ? countryName(countryA2) : "";


      return {
        ...base,
        type: "ESIM",
        productType: "ESIM",
        brandName: ESIM_BRAND,
        uiBrand: ESIM_BRAND,
        uiTitle: makeEsimTitle(regions, countryLabel || countryA2),
        name:
          base.name && base.name.toLowerCase() !== "esim"
            ? base.name
            : makeEsimTitle(regions, countryLabel || countryA2),
        country: countryA2,
        regions,
      };
    } catch {
      return safeNormalize(o);
    }
  });
}, [esims, brands, safeNormalize]);

// 6b) Enrich ALL offers once
  const allEnriched = useMemo(() => {
  return (allOffers || []).map((o) => {
    try {
      const rich = enrichEsimOffer(o, brands);
      const base = rich && typeof rich === "object" ? rich : safeNormalize(o);

      const regions = Array.isArray(base.regions) ? base.regions : [];
      const countryA2 = toA2(o.countryName || o.country || o.countryCode) || "";
      const countryLabel = countryA2 ? countryName(countryA2) : "";


      return {
        ...base,
        type: "ESIM",
        productType: "ESIM",
        brandName: ESIM_BRAND,
        uiBrand: ESIM_BRAND,
        uiTitle: makeEsimTitle(regions, countryLabel || countryA2),
        name:
          base.name && base.name.toLowerCase() !== "esim"
            ? base.name
            : makeEsimTitle(regions, countryLabel || countryA2),
        country: countryA2,
        regions,
      };
    } catch {
      return safeNormalize(o);
    }
  });
}, [allOffers, brands, safeNormalize]);

  // 6c) Decide data source
  // Client-filter for region/subType and for the plan-type tabs. (In the default
  // "all" view, country filtering stays server-side because regional offers have
  // country="" but still cover the selected country via roaming.)
const hasActiveFilter = planType !== "all" || !!(filters.regions || filters.subType);
  const filteredAll = useMemo(() => {
    if (!hasActiveFilter) return allEnriched;
    return allEnriched.filter((p) => {
      const isRegional = !p.country;
      if (planType === "regional" && !isRegional) return false;   // bundles only
      if (planType === "country" && isRegional) return false;     // single-country only
      if (filters.country && p.country !== filters.country) return false;
      if (filters.regions && !(p.regions || []).includes(filters.regions)) return false;
      if (filters.subType && !(p.subTypes || []).includes(filters.subType)) return false;
      return true;
    });
  }, [allEnriched, hasActiveFilter, planType, filters.country, filters.regions, filters.subType]);

  // 6d) Client-side paginate if filtering
  const currentPage = useMemo(
    () => Math.floor((offset || 0) / (limit || 20)) + 1,
    [offset, limit]
  );

  const pageSize = limit || 20;

  const paginatedProducts = useMemo(() => {
    if (!hasActiveFilter) return enrichedEsims;
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredAll.slice(start, end);
  }, [hasActiveFilter, enrichedEsims, filteredAll, currentPage, pageSize]);

  const totalPagesDerived = useMemo(() => {
    if (!hasActiveFilter)
      return Math.max(1, Math.ceil((total || 0) / (limit || 20)));
    return Math.max(1, Math.ceil(filteredAll.length / pageSize));
  }, [hasActiveFilter, total, limit, filteredAll.length, pageSize]);

  const page = currentPage;
  const totalPages = totalPagesDerived;

  const goToPage = useCallback(
    (p) => {
      const pageSize = limit || 20;
      const next = Math.max(1, Number(p) || 1);
      const newOffset = (next - 1) * pageSize;
      setFilters((f) => ({ ...f, offset: Math.max(0, newOffset) }));
    },
    [limit, setFilters]
  );


return (
  <div className="relative">

    {/* 🔍 Floating Mobile Filter Button */}
    {!filterDrawerOpen && (
      <button
        onClick={() => setFilterDrawerOpen(true)}
        className="fixed bottom-4 right-4 bg-primary text-white px-4 py-3 rounded-full shadow-md z-50 sm:hidden inline-flex items-center gap-2 text-sm font-medium"
        aria-label="Open eSIM filters"
      >
        🔍 Filters
      </button>
    )}

    {/* 📱 Mobile Filter Drawer */}
    <EsimFilterDrawer
      isOpen={filterDrawerOpen}
      onClose={() => setFilterDrawerOpen(false)}
      filters={filters}
      onChange={(k, v) => setFilter(k, v)}
      options={options}
      planType={planType}
      onPlanTypeChange={handlePlanTypeChange}
      regionalRegions={regionalRegions}
      onReset={() => {
        setFilter("country", "");
        setFilter("regions", "");
        setFilter("subType", "");
      }}
    />

    {/* 💻 Desktop Grid Layout: Sidebar + Content */}
    <div className="mt-4 lg:grid lg:grid-cols-[280px_1fr] gap-4 items-start">

      {/* 💻 Left Sidebar Filters (Desktop Only) */}
      <aside className="hidden lg:block lg:sticky lg:top-16 h-max">
        <EsimFilters
          filters={filters}
          options={options}
          disabled={loading || !filtersReady}
          onChange={(k, v) => setFilter(k, v)}
          planType={planType}
          onPlanTypeChange={handlePlanTypeChange}
          regionalRegions={regionalRegions}
        />
      </aside>

      {/* 📦 Main Content */}
      <section className="space-y-4">

        {/* ⏳ Loading bar */}
        {(loading || allLoading) && (
          <div className="h-1 w-full bg-surfaceElevated overflow-hidden rounded">
            <div className="h-full w-1/3 bg-primary/60 animate-progress" />
          </div>
        )}

        {/* 🧠 Filter status */}
        <div className="text-xs text-textSecondary h-4" aria-live="polite">
          {allLoading && !allError && "Building filters…"}
          {!!allError && (
            <span className="text-error">
              Filter data failed to load. You can still browse.
            </span>
          )}
        </div>

        {/* 📶 Info Box */}
        <div className="mb-2">
          <h2 className="text-xl font-semibold mb-2">📶 eSIM Offers</h2>
          <div className="rounded-lg border border-warning/30 bg-warning/10 text-warning p-2 text-xs">
            <strong>eSIM compatibility required.</strong> Device must be{" "}
            <em>eSIM-capable</em> and <em>carrier-unlocked</em>. If your phone has
            1 physical SIM + 1 eSIM, keep your primary carrier on the physical SIM
            and install the roaming eSIM in the eSIM slot. Use strong Wi-Fi during
            install.
            <div className="mt-1">
               <Link
   href="/esim/install#compatibility"
   className="underline font-medium"
   onClick={(e) => e.stopPropagation()}
 >
    Open the eSIM guide →
  </Link>
            </div>
          </div>
        </div>

        {/* 🛒 Product List */}
        <ProductList
          productType="ESIM"
          selectedProduct={selectedProduct}
          onSelect={onSelect}
          page={page}
          totalPages={totalPages}
          onPageChange={goToPage}
          products={paginatedProducts}
          loading={loading}
          error={error}
          emptyLabel={
            error
              ? "Failed to load offers"
              : loading
              ? "Loading offers…"
              : allLoading
              ? "Preparing filters…"
              : "No eSIM offers found"
          }
          user={user}
          piRate={Number.isFinite(piRate) && piRate > 0 ? piRate : undefined}
          feePercent={feePercent}
        />
      </section>
    </div>
  </div>
);

}
